<template>
  <div class="soar-container flex flex-col gap-y-4">
    <ExternalLinks></ExternalLinks>

    <DashboardControlpanel>
      <template #header="{ header }">
        <div class="establishment-header w-full flex flex-col gap-3">
          <div class="w-full flex justify-between border-b border-brand-gray-300">
            <h2 class="text-xl md:text-2xl text-primary font-medium">
              {{ $t('controlPanel') }}
            </h2>
          </div>
          <div class="w-full flex flex-wrap md:flex-shrink-0 md:flex-nowrap gap-2">
            <div class="flex-1 flex gap-2 flex-wrap md:flex-nowrap">
              <RouterLink
                :to="{ name: 'EstUserManagementLanding' }"
                class="pe-1.5 rounded-md bg-primary text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
              >
                <div class="flex justify-center items-center h-full p-2 rounded-s bg-black/10">
                  <faUsers class="w-5 fill-white" />
                </div>
                <span class="px-2 tracking-wide text-white text-sm pb-[1px]">
                  {{ $t('estabSubUsersManagment') }}
                </span>
              </RouterLink>

              <RouterLink
                :to="{ name: 'EstAddChild' }"
                class="pe-1.5 rounded-md bg-primary text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
              >
                <div class="flex justify-center items-center h-full p-2 rounded-s bg-black/10">
                  <FontAwesomeIcon :icon="faPlusSquare" class="w-5 text-white" />
                </div>
                <span class="px-2 tracking-wide text-white text-sm pb-[1px]">
                  {{ $t('addFreeZoneEstablishment') }}
                </span>
              </RouterLink>

              <div class="px-1.5 rounded-md bg-brand-grayblue-300 text-white flex items-center">
                <span class="tracking-wide flex-1 text-white text-sm pb-[1px]">
                  {{ $t('blackPoints') }}
                  <span class="ps-2 text-primary">{{ header?.blackPoints || 0 }}</span>
                </span>
              </div>

              <div class="px-1.5 rounded-md bg-brand-grayblue-300 text-white flex items-center">
                <span class="tracking-wide flex-1 text-white text-sm pb-[1px]">
                  {{ $t('lastSyncDateTime') }}
                  <span class="ps-2 text-primary">
                    {{
                      header?.lastSyncDateTime &&
                      DateTime.fromJSDate(new Date(header?.lastSyncDateTime)).toFormat('t')
                    }}</span
                  >
                </span>
              </div>
            </div>

            <button
              class="p-4 md:p-2 rounded-md bg-primary text-white flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
              @click="onRefresh"
            >
              <FontAwesomeIcon :icon="faRefresh" class="w-3 text-white" />
            </button>
          </div>
        </div>
      </template>
    </DashboardControlpanel>
    <div class="flex flex-col gap-y-1">
      <div class="flex justify-between border-b border-brand-gray-300">
        <h2 class="text-xl md:text-2xl text-primary font-medium">
          {{ $t('smartservice') }}
        </h2>
        <div class="flex gap-x-3">
          <button @click="selectedView = 'list'">
            <faGripLines
              :class="[selectedView == 'list' ? 'fill-primary' : 'fill-brand-black-secondary']"
              class="w-5"
            />
          </button>
          <button @click="selectedView = 'grid'">
            <faGrip
              :class="[selectedView == 'grid' ? 'fill-primary' : 'fill-brand-black-secondary']"
              class="w-5"
            />
          </button>
        </div>
      </div>
      <DashboardServiceDrillDown
        v-if="!isLoading"
        :selection-filter="selectionFilter"
        :regions="dashboardStore.regions"
        :modules="dashboardStore.modules"
        :services="dashboardStore.services"
        :sub-services="dashboardStore.subServices"
        @on-region-change="dashboardStore.handleRegionChange"
        @on-module-change="dashboardStore.handleModuleChange"
        @on-service-change="dashboardStore.handleServiceChange"
        @on-subservice-change="dashboardStore.handleSubserviceChange"
      ></DashboardServiceDrillDown>
      <ServiceDirectory
        v-if="!isLoading && dashboardStore.directoryItems.length > 0"
        :key="isLoading"
        :services="dashboardStore.directoryItems"
        :view-as="selectedView"
      >
        <template #gridCardCorner="{ service }">
          <div class="flex gap-x-3">
            <button
              class="w-fit pe-1 rounded-md border border-brand-gray-600 bg-white flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
              @click="onAddFavorite(service)"
            >
              <div class="flex justify-center items-center h-full bg-black/10 p-1 rounded-s">
                <faStarOutline class="w-4 fill-white" />
              </div>
              <span
                class="ps-2 pe-1 ltr:font-sans tracking-wide text-brand-black-autofill text-xs pb-[1px]"
              >
                {{ $t('favairate') }}
              </span>
            </button>
          </div>
        </template>
        <template #gridCardActionsS="{ service }">
          <button
            class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
            @click="onStartService(service)"
          >
            <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
              {{ $t('serviceStart') }}
            </span>
            <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e">
              <FontAwesomeIcon
                :icon="faArrowCircleRight"
                class="w-4 fill-primary/80 hover:fill-primary text-white"
              />
            </div>
          </button>
        </template>
        <template #gridCardActionsE="{ service }">
          <RouterLink
            :to="{ name: 'ServiceCardView', params: { serviceId: service.transactionId } }"
            target="_blank"
            class="w-fit pe-1 rounded-md bg-blue-500/80 hover:bg-blue-500 flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed font-normal"
          >
            <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-s">
              <faListAlt class="w-4 fill-white" />
            </div>
            <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
              {{ $t('serviceCard') }}
            </span>
          </RouterLink>
        </template>
        <template #rowActions="{ service }">
          <button
            class="w-fit pe-1 rounded-md border border-brand-gray-600 bg-white flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
            @click="onAddFavorite(service)"
          >
            <div class="flex justify-center items-center h-full bg-black/10 p-1 rounded-s">
              <faStarOutline class="w-4 fill-white" />
            </div>
            <span
              class="ps-2 pe-1 ltr:font-sans tracking-wide text-brand-black-autofill text-xs pb-[1px]"
            >
              {{ $t('favairate') }}
            </span>
          </button>
          <RouterLink
            :to="{ name: 'ServiceCardView', params: { serviceId: service.transactionId } }"
            target="_blank"
            class="w-fit pe-1 rounded-md bg-blue-500/80 hover:bg-blue-500 flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
          >
            <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-s">
              <faListAlt class="w-4 fill-white" />
            </div>
            <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
              {{ $t('serviceCard') }}
            </span>
          </RouterLink>
          <button
            class="w-fit ps-1 rounded-md bg-primary/80 hover:bg-primary flex items-center justify-between hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
            @click="onStartService(service)"
          >
            <span class="ps-2 pe-1 ltr:font-sans tracking-wide text-white text-xs pb-[1px]">
              {{ $t('serviceStart') }}
            </span>
            <div class="flex justify-center items-center h-full bg-black/10 p-1.5 rounded-e">
              <FontAwesomeIcon
                :icon="faArrowCircleRight"
                class="w-4 fill-primary/80 hover:fill-primary text-white"
              />
            </div>
          </button>
        </template>
      </ServiceDirectory>
    </div>
  </div>
</template>

<script setup>
import faGrip from '@/assets/icons/faGrip.svg?component'
import faGripLines from '@/assets/icons/faGripLines.svg?component'
import faListAlt from '@/assets/icons/faListAlt.svg?component'
import faStarOutline from '@/assets/icons/faStarOutline.svg?component'
import faUsers from '@/assets/icons/faUsers.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import DashboardServiceDrillDown from '@/components/Dashboard/DashboardServiceDrillDown.vue'
import ServiceDirectory from '@/components/search/ServiceDirectory.vue'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import DashboardControlpanel from '@/views/establishment/dashboard/ControlPanel.vue'
import ExternalLinks from '@/views/establishment/dashboard/ExternalLinks.vue'
import { faArrowCircleRight, faPlusSquare, faRefresh } from '@fortawesome/free-solid-svg-icons'
import { DateTime } from 'luxon'
import { useForm } from 'vee-validate'
import { nextTick, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const globalStore = useGlobalStore()
const authStore = useAuthStore()
const formStore = useFormStore()
const defaultStartDate = ref(DateTime.now().minus({ days: 30 }).toFormat('dd/MM/yyyy'))
const defaultToDate = ref(DateTime.now().toFormat('dd/MM/yyyy'))
const dashboardStore = useDashboardStore()

const isLoading = ref(true)
const selectedView = ref('list')

const selectionFilter = reactive({
  region: 1
})

formStore.$reset()
const form = useForm({
  initialValues: {
    showFromDate: defaultStartDate.value,
    showToDate: defaultToDate.value
  }
})
formStore.form = form

onMounted(async () => {
  await nextTick()
  await fetchData()
})

const onRefresh = async () => {
  await fetchData()
}

const fetchData = async () => {
  isLoading.value = true
  await authStore.getProfile()
  await authStore.fetchEstablishmentInfo()

  if (authStore.user.isAuthorityEstablishment && !authStore.isFreezone) {
    // TODO: GetByIdForHolding
  }

  dashboardStore.selectionFilter.region = authStore.user.establishmentAdminRegionId
  await dashboardStore.fetchAdminRegions()
  await dashboardStore.fetchServiceTransactions()
  isLoading.value = false
}

const onStartService = async (service) => {
  const { transactionId } = service

  const {
    mohreWorkPackagePrentServiceIds,
    ServiceTransactions: { seaPortShipTripService, preEntryExtendVisa },
    seaPortServicesNeedRequest
  } = client_lookups

  if (mohreWorkPackagePrentServiceIds.includes(transactionId)) {
    // const returnEarly = await handleServicesWithMohreUserAuth(transactionId)
    // if (returnEarly) return
    const response = await axiosInstance.get(
      '/api/establishment/mohreWorkPackage/request/checkMohreUserAuthorization'
    )

    if (!response) {
      const query = {}

      // TODO if childEstablishment exists, add to query

      router.push({
        name: 'EstValidateSubUsers',
        params: {
          transactionId
        },
        query
      })

      return
    }
  }

  if (transactionId == seaPortShipTripService) {
    return router.push({ name: 'ShipRequestServices' })
  }

  if (seaPortServicesNeedRequest.includes(transactionId)) {
    console.log('TODO: seaPortServicesNeedRequest logic')
    // TODO: navigate to ship request services $location.path('/requests?moduleId=' + window.lookups.module.seaPort);
  }

  if (transactionId == preEntryExtendVisa) {
    console.log('TODO: preEntryExtendVisa logic')
    /**
     * TODO
     * check if this account is from a child establishment
     * then redirect to oneClickServiceList
     *
     * {
     *   name: 'oneClickServiceList',
     *   query: { childEstablishmentId, serviceTransId }
     * }
     *
     * else
     * {
     *   name: 'oneClickServiceList',
     *   query: { childEstablishmentId, serviceTransactionId }
     * }
     */
  }

  const config = await globalStore.fetchConfig(transactionId)

  const {
    redirectToDeplomaticCards,
    requiredEmployee,
    isDelegatesListRequired,
    showLeavePermitPopup,
    serviceConfig: { freeZoneReportFlag }
  } = config

  if (redirectToDeplomaticCards) {
    // TODO: navigate to diplomaticCards $state.go('diplomaticCards', { serviceTransactionId: transaction.id });
  }

  if (requiredEmployee) {
    const {
      selectionFilter: { module }
    } = dashboardStore
    return router.push({
      name: 'EstSponsoredRequests',
      query: { moduleId: module?.id, serviceId: transactionId, withException: false }
    })
    /**
     * TODO
     * check if this account is from a child establishment
     * then redirect to employee
     *
     * if childEstablishment
     * $state.go("employee", {
     *   transactionId: transaction.id,
     *   moduleId: $scope.selectedModule.id,
     *   withException: $scope.withException ? true : false,
     *   childEstablishmentId: $scope.childEstablishmentId,
     * });
     *
     * else
     *
     * $state.go("employee", {
     *   transactionId: transaction.id,
     *   moduleId: $scope.selectedModule.id,
     *   withException: $scope.withException ? true : false,
     * });
     */
  }

  if (isDelegatesListRequired) {
    /**
     * TODO
     * check if this account is from a child establishment
     * then redirect to employee
     *
     * if childEstablishment
     * $state.go("employee", {
     *   transactionId: transaction.id,
     *   moduleId: $scope.selectedModule.id,
     *   withException: $scope.withException ? true : false,
     *   childEstablishmentId: $scope.childEstablishmentId,
     * });
     *
     * else
     *
     * $state.go("employee", {
     *   transactionId: transaction.id,
     *   moduleId: $scope.selectedModule.id,
     *   withException: $scope.withException ? true : false,
     * });
     */
  }

  if (showLeavePermitPopup) {
    // TODO: show popup
  }

  if (freeZoneReportFlag) {
    return router.push({ name: 'FreezoneReportCard' })
  }

  router.push({
    name: 'ServiceForm',
    params: { serviceId: transactionId, withException: false }
  })
}

async function fetchServices() {
  const params = {
    includeHiddenServices: false,
    isChildSeleted: false,
    serviceId: null,
    parentServiceId: null,
    sponsorFileTypeId: null
  }
  const modules = await axiosInstance.get('/api/application/service/transactions', { params })

  const services = []

  modules.forEach((module) => {
    module.moduleServices.forEach((parentService) => {
      parentService.childs.forEach((subService) => {
        subService.serviceTransactions.forEach((transaction) => {
          const serviceNameAsList = [
            module.localizedName,
            parentService.localizedName,
            subService.localizedName,
            transaction.localizedName
          ]

          services.push({
            localizedServiceName: serviceNameAsList.join('-'),
            moduleId: module.id,
            transactionId: transaction.id,
            favactive: ''
          })
        })
      })
    })
  })

  return services
}
</script>

<style lang="scss" scoped></style>
